import { useCallback, useState } from 'react';
import * as settingsService from '../services/settingsService';
import { useUIStore } from '../stores/uiStore';
import {
  IUserSettings,
  IUserSettingsUpdateRequest,
} from '../types/interfaces/ISettings';
import { ApiResponse } from '../services/types';
import { getApiErrorMessage } from '../services/core/apiResponseHelpers';
import { notifyApiSuccess } from '../services/core/apiSuccessUX';

export const useSettings = () => {
  const [loading, setLoading] = useState(false);
  const { addNotification } = useUIStore();

  const getMySettings = useCallback(async (): Promise<ApiResponse<IUserSettings>> => {
    setLoading(true);
    try {
      const response = await settingsService.getMySettings();
      if (!response.success) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: getApiErrorMessage(response, 'Error al cargar la configuración'),
        });
      }
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Error al cargar la configuración';
      addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      });
      return {
        success: false,
        data: null as unknown as IUserSettings,
        error: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  const updateMySettings = useCallback(
    async (
      settings: IUserSettingsUpdateRequest
    ): Promise<ApiResponse<IUserSettings>> => {
      setLoading(true);
      try {
        const response = await settingsService.updateMySettings(settings);
        if (response.success) {
          notifyApiSuccess(addNotification, 'Configuración Guardada', 'Los cambios se han aplicado correctamente');
        } else {
          addNotification({
            type: 'error',
            title: 'Error',
            message: getApiErrorMessage(response, 'Error al guardar la configuración'),
          });
        }
        return response;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Error al guardar la configuración';
        addNotification({
          type: 'error',
          title: 'Error',
          message: errorMessage,
        });
        return {
          success: false,
          data: null as unknown as IUserSettings,
          error: errorMessage,
        };
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  const resetMySettings = useCallback(async (): Promise<ApiResponse<IUserSettings>> => {
    setLoading(true);
    try {
      const response = await settingsService.resetMySettings();
      if (response.success) {
        addNotification({
          type: 'success',
          title: 'Configuración Restaurada',
          message: 'Se restauraron los valores por defecto',
        });
      } else {
        addNotification({
          type: 'error',
          title: 'Error',
          message: getApiErrorMessage(response, 'Error al restaurar la configuración'),
        });
      }
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error al restaurar la configuración';
      addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      });
      return {
        success: false,
        data: null as unknown as IUserSettings,
        error: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  return {
    getMySettings,
    updateMySettings,
    resetMySettings,
    loading,
  };
};